import { useCallback } from "react";

/**
 * The WAI-ARIA radiogroup keyboard contract, in one place.
 *
 * Arrow keys move between options and select as they go ("selection follows
 * focus"), Home/End jump to the ends, Enter/Space select without moving, and a
 * roving tabindex keeps exactly ONE option in the tab order so the group is a
 * single Tab stop.
 *
 * This already existed, hand-copied, in NumericField/UnitToggle.tsx and
 * NumericField/SlippageControl.tsx. This is the extraction, written for the third
 * caller rather than as a third copy. The two originals are deliberately left
 * alone for now: they ship, e2e covers them, and swapping them is a separate
 * change with its own risk.
 *
 * One thing the copies do not do: per-option `disabled`. They only handle the
 * whole control being disabled, so arrowing onto a disabled option there would
 * select it. Here, movement skips disabled options.
 *
 * Note the global KeyboardManager already stands down for role="radio" /
 * role="radiogroup" (ARROW_OWNING_ROLES), so a caller that sets those roles gets
 * arrow keys without the page's own shortcuts firing too.
 */

/** Modulo that is safe for negative indexes, so Home/End can be expressed as moves. */
export const wrapIndex = (index: number, length: number): number =>
  ((index % length) + length) % length;

export interface RovingRadioGroupOptions<T extends string | number> {
  /** Option values, in the order they are rendered. */
  values: readonly T[];
  /** The selected value. */
  value: T;
  /** Select an option. */
  onSelect: (value: T) => void;
  /**
   * Move DOM focus to an option's control. Given both the value and the index
   * because callers track their refs by different keys: SegmentedControl keys
   * them by value, UnitToggle and SlippageControl by index.
   */
  focus: (value: T, index: number) => void;
  /** Options that cannot be selected, and that movement skips over. */
  isDisabled?: (value: T) => boolean;
}

export interface RovingRadioItemProps {
  role: "radio";
  "aria-checked": boolean;
  "aria-disabled": true | undefined;
  tabIndex: 0 | -1;
  onKeyDown: (event: React.KeyboardEvent) => void;
}

export function useRovingRadioGroup<T extends string | number>({
  values,
  value,
  onSelect,
  focus,
  isDisabled,
}: RovingRadioGroupOptions<T>) {
  const disabled = useCallback(
    (v: T) => (isDisabled ? isDisabled(v) : false),
    [isDisabled]
  );

  /**
   * The option that holds tabIndex 0. Normally the selected one; if the selected
   * value is not in `values` (or nothing is selected yet) the first enabled
   * option takes it, because a group where every option is -1 cannot be tabbed
   * into at all.
   */
  const selected = values.indexOf(value);
  const tabStop =
    selected >= 0 ? selected : Math.max(0, values.findIndex((v) => !disabled(v)));

  const move = useCallback(
    (from: number, delta: number) => {
      if (!values.length) return;
      // Step until an enabled option turns up; give up after a full lap so a
      // group where everything is disabled cannot spin.
      for (let step = 1; step <= values.length; step += 1) {
        const next = wrapIndex(from + delta * step, values.length);
        const candidate = values[next];
        if (!disabled(candidate)) {
          onSelect(candidate);
          focus(candidate, next);
          return;
        }
      }
    },
    [values, disabled, onSelect, focus]
  );

  const itemProps = useCallback(
    (optionValue: T, index: number): RovingRadioItemProps => ({
      role: "radio",
      "aria-checked": optionValue === value,
      // aria-disabled, not the disabled attribute: the option stays in the
      // accessibility tree so the group's shape does not change under a reader.
      "aria-disabled": disabled(optionValue) || undefined,
      tabIndex: index === tabStop ? 0 : -1,
      onKeyDown: (event: React.KeyboardEvent) => {
        switch (event.key) {
          case "ArrowRight":
          case "ArrowDown":
            event.preventDefault();
            move(index, 1);
            break;
          case "ArrowLeft":
          case "ArrowUp":
            event.preventDefault();
            move(index, -1);
            break;
          case "Home":
            event.preventDefault();
            // From one before the start, step forward: lands on the first
            // enabled option rather than assuming index 0 is enabled.
            move(-1, 1);
            break;
          case "End":
            event.preventDefault();
            move(0, -1);
            break;
          case "Enter":
          case " ":
            event.preventDefault();
            if (!disabled(optionValue)) onSelect(optionValue);
            break;
          default:
            break;
        }
      },
    }),
    [value, disabled, tabStop, move, onSelect]
  );

  return { itemProps };
}
